import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import axiosInstance from './axios';

// API/Components
import {
	ANALYSIS_RESULT,
	DATA_ERROR_VALUE,
	GRAPH_BOUNDS,
	GRAPH_STYLE,
	isEmpty,
	validValue,
	AnalysisResultToStateColor,
	formatDateTime,
	measUnitsConversion,
	getUserTempUnits
} from './enums';
import { DrawHorizontalDashedLine, DrawVerticalDashedLineAtTooltip } from './chart_helpers';

// Material UI
import Button from '@mui/material/Button';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import Grid from '@mui/material/Grid';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	Filler,
	LineController,
	ScatterController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import tinycolor from 'tinycolor2';

// Register Chart.JS Plugins
ChartJS.register(
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	Filler,
	LineController,
	ScatterController,
	zoomPlugin,
);


export default function UnitTrendGraph(props) {
	const {
		trendData,
		limitData,
		graphScalesData,
		selectedDateRange,
		selectedString,
		selectedUnit,
		graphType,
		loading
	} = props;

	const { locationNum } = useParams();
	
	const chartRef = useRef(null);
	
	const [graphData, setGraphData] = useState(null);
	const [graphSettings, setGraphSettings] = useState({
		limitsEnabled: true,
		maxminEnabled: false,
		avgEnabled: false,
		initialsEnabled: true,
	});
	const [graphStyle, setGraphStyle] = useState(GRAPH_STYLE.AREA);

	const [maxAvgMinData, setMaxAvgMinData] = useState({});
	const [graphOptions, setGraphOptions] = useState({
		maintainAspectRatio: false,
		animation: false,
		options: {
			responsive: true,
		},
		transitions: {
			zoom: {
				animation: {
					duration: 0,
				},
			},
		},
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				intersect: false,
				// axis: 'x',
				mode: 'index',
			},
			zoom: {
				pan: {
					enabled: true,
					mode: 'xy',
				},
				limits: {
					x: {
						minRange: 2,
					},
					y: {
						min: 0,
						max: 100,
						minRange: 5,
					}
				},
				zoom: {
					mode: 'y',
					wheel: {
						enabled: true,
						speed: 0.02,
					},
					pinch: {
						enabled: true,
					},
					onZoomStart: (e) => {
						if (typeof e.chart.config?.options?.plugins?.zoom?.zoom === "undefined") {
							return;
						}
						if (e.event.shiftKey === true) {
							e.chart.config.options.plugins.zoom.zoom.mode = "x";
							e.chart.config.options.plugins.zoom.zoom.scaleMode = "x";
						} else {
							e.chart.config.options.plugins.zoom.zoom.mode = "y";
							e.chart.config.options.plugins.zoom.zoom.scaleMode = "y";
						}
					},
				}
			},
		},
	});
	
	// const [isLoading, setIsLoading] = useState(loading);

	// Keep track of the limits data for drawing the limit lines
	const limitDataRef = useRef(null);
	useEffect(() => {
		limitDataRef.current = limitData;
	}, [limitData]);
	
	// Keep track of the graph type for drawing limit lines
	const graphTypeRef = useRef(null);
	useEffect(() => {
		graphTypeRef.current = graphType;
	}, [graphType]);
	
	// Keep track of the graph settings for drawing limit lines
	const graphSettingsRef = useRef(null);
	useEffect(() => {
		graphSettingsRef.current = graphSettings;
	}, [graphSettings]);

	// Keep track of selected string and unit for drawing limit lines
	const selectedStringRef = useRef(null);
	const selectedUnitRef = useRef(null);
	useEffect(() => {
		selectedStringRef.current = selectedString;
		selectedUnitRef.current = selectedUnit;
	}, [selectedString, selectedUnit]);

	// Clear graph data when selectedString or selectedUnit changes
	useEffect(() => {
		setGraphData(null);
	}, [selectedString, selectedUnit]);

	// Get the list of max/avg/min data for the specified location id and string number
	useEffect(() => {
		if (selectedString !== undefined && selectedString !== null) {
			axiosInstance.get(`/loc/${locationNum}/stats_flat/string_${selectedString}?filter=${selectedDateRange}`).then(response => {
				setMaxAvgMinData(response.data);
			});
		}
	}, [locationNum, selectedDateRange, selectedString]);

	// Update the graph data sets
	useEffect(() => {
		if (typeof trendData != "undefined" && trendData !== null && !isEmpty(trendData)) {

			const unitsData = GetGraphData(trendData, graphType);
			if (unitsData !== undefined && unitsData !== null) {
				setGraphData(unitsData);
			}
		}
	}, [trendData, selectedDateRange, selectedString, selectedUnit, graphType, graphSettings, graphStyle, limitData]);

	useEffect(() => {
		const graphScales = SetGraphYScale(graphType);
		if (graphScales !== undefined && graphScales !== null) {
			setGraphOptions((prevState) => {
				return {
					...prevState,
					scales: {
						y: graphScales,
						x: {
							title: {
								display: false,
							}
						}
					}
				}
			});
		}
	}, [graphScalesData, graphType, selectedString, selectedUnit]);
	
	useEffect(() => {
		SetGraphBounds();
	}, [graphType]);

	const graphPlugins = [
		{
			afterDraw: chart => DrawVerticalDashedLineAtTooltip(chart),
		},
		{
			afterDatasetDraw: chart => {
				let graphType = graphTypeRef.current;
				let graphSettings = graphSettingsRef.current;
				let limitData = limitDataRef.current;

				if (limitData != undefined && limitData != null && !isEmpty(limitData)) {
					// If enabled, draw a horizontal dashed line at the various limit values
					if (graphSettings.limitsEnabled) {
						const limits = GetLimitsData(limitData, graphType);
						if (limits != undefined && limits != null && limits.length > 0) {
							limits.forEach((limit) => {
								DrawHorizontalDashedLine(chart, limit.value, limit.color);
							});
						}
					}

					// If enabled, draw a horizontal dashed line at the initial impedance value on the impedance graph
					if (graphType === 1 && graphSettings.initialsEnabled) {
						const selectedStr = selectedStringRef.current;
						const selectedUnt = selectedUnitRef.current;
						const unitLimits = limitData.units.find(unit => unit.str_nbr === selectedStr && unit.unit_nbr === selectedUnt);
						if (unitLimits) {
							let initialImped = unitLimits.imped.initial;
							if (initialImped !== undefined && initialImped > DATA_ERROR_VALUE.INVALID_VALUE) {
								DrawHorizontalDashedLine(chart, initialImped, 'rgba(0, 128, 0, 0.9)');
							}
						}
					}
				}
			}
		}
	];

	function SetGraphBounds() {
		let graphZoom = GRAPH_BOUNDS.UNIT_VOLTAGE;
		switch(graphType) {
			case 0:  // Voltage
				graphZoom = GRAPH_BOUNDS.UNIT_VOLTAGE;
				break;

			case 1:  // Impedance
				graphZoom = GRAPH_BOUNDS.UNIT_IMPEDANCE;
				break;

			case 2:  // Temperature
				graphZoom = {
					...GRAPH_BOUNDS.TEMPERATURE,
					bounds_max: measUnitsConversion(GRAPH_BOUNDS.TEMPERATURE.bounds_max),
					bounds_min: measUnitsConversion(GRAPH_BOUNDS.TEMPERATURE.bounds_min),
				};
				break;

			case 3:  // Differential Impedance
				graphZoom = GRAPH_BOUNDS.DIFF_IMPEDANCE;
				break;

			default:
				graphZoom = GRAPH_BOUNDS.UNIT_VOLTAGE;
				break;
		}
		
		setGraphOptions((prevState) => {
			return {
				...prevState,
				plugins: {
					...prevState.plugins,
					zoom: {
						...prevState.plugins.zoom,
						limits: {
							...prevState.plugins.zoom.limits,
							y: {
								max: graphZoom.bounds_max,
								min: graphZoom.bounds_min,
								minRange: graphZoom.min_range
							},
						},
					},
				},
			}
		});
	}

	function GetLimitsData(limitData, graphType) {
		let critHiLimit = DATA_ERROR_VALUE.INVALID_VALUE;
		let maintHiLimit = DATA_ERROR_VALUE.INVALID_VALUE;
		let maintLoLimit = DATA_ERROR_VALUE.INVALID_VALUE;
		let critLoLimit = DATA_ERROR_VALUE.INVALID_VALUE;

		// Find the limits for the selected unit - use refs to get current values
		const currentString = selectedStringRef.current;
		const currentUnit = selectedUnitRef.current;
		
		const unitLimits = limitData.units.find(unit => unit.str_nbr === currentString && unit.unit_nbr === currentUnit);

		if (limitData != undefined && limitData != null && !isEmpty(limitData) &&
			graphType != undefined && graphType != null) {
			switch(graphType) {
				case 0:
					critHiLimit = unitLimits.volt.hi_crit;
					maintHiLimit = unitLimits.volt.hi_maint;
					maintLoLimit = unitLimits.volt.lo_maint;
					critLoLimit = unitLimits.volt.lo_crit;
					break;
				case 1:
					critHiLimit = unitLimits.imped.hi_crit;
					maintHiLimit = unitLimits.imped.hi_maint;
					break;
				case 2:
					critHiLimit = measUnitsConversion(limitData.system.temp_hi);
					critLoLimit = measUnitsConversion(limitData.system.temp_lo);
					break;
				case 3:
					critHiLimit = unitLimits.diff_imp.hi_crit;
					maintHiLimit = unitLimits.diff_imp.hi_maint;
					break;
				default:
					critHiLimit = unitLimits.volt.hi_crit;
					maintHiLimit = unitLimits.volt.hi_maint;
					maintLoLimit = unitLimits.volt.lo_maint;
					critLoLimit = unitLimits.volt.lo_crit;
					break;
			}
		}

		// Get the associated limit colors
		let hiCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS_CRITICAL);
		let hiMaintColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS);
		let loMaintColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS);
		let loCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS_CRITICAL);

		let limitSets = [];
		if (critHiLimit !== undefined && critHiLimit > DATA_ERROR_VALUE.INVALID_VALUE) {
			limitSets.push({
				value: critHiLimit,
				color: hiCritColor
			});
		}

		if (maintHiLimit !== undefined && maintHiLimit > DATA_ERROR_VALUE.INVALID_VALUE) {
			limitSets.push({
				value: maintHiLimit,
				color: hiMaintColor
			});
		}

		if (maintLoLimit !== undefined && maintLoLimit > DATA_ERROR_VALUE.INVALID_VALUE) {
			limitSets.push({
				value: maintLoLimit,
				color: loMaintColor
			});
		}

		if (critLoLimit !== undefined && critLoLimit > DATA_ERROR_VALUE.INVALID_VALUE) {
			limitSets.push({
				value: critLoLimit,
				color: loCritColor
			});
		}
		
		return limitSets;
	}

	function GetMaxAvgMinData(graphType) {
		let maxValues = [];
		let avgValues = [];
		let minValues = [];
		
		if (maxAvgMinData !== undefined && maxAvgMinData !== null && !isEmpty(maxAvgMinData)) {
			switch(graphType) {
				case 0:
					maxValues = maxAvgMinData.volt_max;
					avgValues = maxAvgMinData.volt_avg;
					minValues = maxAvgMinData.volt_min;
					break;
				case 1:
					maxValues = maxAvgMinData.imp_max;
					avgValues = maxAvgMinData.imp_avg;
					minValues = maxAvgMinData.imp_min;
					break;
				case 2:
					maxValues = measUnitsConversion(maxAvgMinData.temp_max);
					avgValues = measUnitsConversion(maxAvgMinData.temp_avg);
					minValues = measUnitsConversion(maxAvgMinData.temp_min);
					break;
				case 3:
					maxValues = [];
					avgValues = [];
					minValues = [];
					break;
				default:
					maxValues = [];
					avgValues = [];
					minValues = [];
					break;
			}
		}
		
		// Get the associated limit colors
		let maxColor = '#0033FF';
		let avgColor = '#005500';
		let minColor = '#0033FF';

		let statSets = {
			'maxSet': {},
			'avgSet': {},
			'minSet': {}
		};
		if (maxValues !== undefined && maxValues.length > 0) {
			statSets.maxSet = {
				type: 'scatter',
				label: 'Measurement Maximum for String',
				backgroundColor: maxColor + "55",
				borderColor: maxColor + "77",
				borderWidth: 1,
				pointStyle: 'triangle',
				radius: 4,
				rotation: 180,
				hitRadius: 5,
				data: maxValues,
				order: 4,
			};
		}
		
		if (avgValues !== undefined && avgValues.length > 0) {
			statSets.avgSet = {
				type: 'scatter',
				label: 'Measurement Average for String',
				backgroundColor: avgColor + "55",
				borderColor: avgColor + "77",
				borderWidth: 1,
				pointStyle: 'crossRot',
				radius: 5,
				hitRadius: 5,
				data: avgValues,
				order: 5,
			};
		}

		if (minValues !== undefined && minValues.length > 0) {
			statSets.minSet = {
				type: 'scatter',
				label: 'Measurement Minimum for String',
				backgroundColor: minColor + "55",
				borderColor: minColor + "77",
				borderWidth: 1,
				pointStyle: 'triangle',
				radius: 4,
				rotation: 0,
				hitRadius: 5,
				data: minValues,
				order: 6,
			};
		}

		return statSets;
	}

	function SetGraphYScale(graphType) {
		
		// Find the graph scales for the selected unit
		const unitScale = graphScalesData.find(unit => unit.string_nbr === selectedString && unit.unit_nbr === selectedUnit);

		if (unitScale !== undefined && unitScale !== null) {

			let max = DATA_ERROR_VALUE.INVALID_VALUE;
			let min = DATA_ERROR_VALUE.INVALID_VALUE;
			let title = "";
			
			switch (graphType) {
				case 0:  // Voltage
					max = validValue(unitScale?.voltage?.max, GRAPH_BOUNDS.UNIT_VOLTAGE.default_max);
					min = validValue(unitScale?.voltage?.min, GRAPH_BOUNDS.UNIT_VOLTAGE.default_min);
					title = "Unit Voltage (VDC)";
					break;

				case 1:  // Impedance
					max = validValue(unitScale?.impedance?.max, GRAPH_BOUNDS.UNIT_IMPEDANCE.default_max);
					min = validValue(unitScale?.impedance?.min, GRAPH_BOUNDS.UNIT_IMPEDANCE.default_min);
					title = "Unit Impedance (mΩ)";
					break;

				case 2:  // Temperature
					const maxC = validValue(unitScale?.temperature?.max, GRAPH_BOUNDS.TEMPERATURE.default_max);
					const minC = validValue(unitScale?.temperature?.min, GRAPH_BOUNDS.TEMPERATURE.default_min);
					max = measUnitsConversion(maxC);
					min = measUnitsConversion(minC);
					title = 'Unit Temperature (' + getUserTempUnits() + ')';
					break;

				case 3:  // Differential Impedance
					max = GRAPH_BOUNDS.DIFF_IMPEDANCE.default_max;
					min = GRAPH_BOUNDS.DIFF_IMPEDANCE.default_min;
					title = "Unit Differential Impedance (%)";
					break;

				default:  // Voltage
					max = validValue(unitScale?.voltage?.max, GRAPH_BOUNDS.UNIT_VOLTAGE.default_max);
					min = validValue(unitScale?.voltage?.min, GRAPH_BOUNDS.UNIT_VOLTAGE.default_min);
					title = "Unit Voltage (VDC)";
					break;
			}

			if (max !== DATA_ERROR_VALUE.INVALID_VALUE && min !== DATA_ERROR_VALUE.INVALID_VALUE) {
				return {
					max: max,
					min: min,
					title: {
						display: true,
						text: title,
						font: {
							family: "Saira, sans-serif",
							size: 18,
							weight: 600,
						},
					},
				};
			}
		}

		return {};
	}

	function GetUnitInfo(unit, graphType) {
		let value, analysis = 0;
		switch(graphType) {
			case 0:
				value = unit.measurement.unit_voltage?.toFixed(3);
				analysis = (unit.analysis.voltage_analysis_valid) ?
					unit.analysis.voltage_analysis_result :	0;
				break;
			case 1:
				value = unit.measurement.unit_imp?.toFixed(3);
				analysis = (unit.analysis.impedance_analysis_valid) ?
					unit.analysis.impedance_analysis_result : 0;
				break;
			case 2:
				let temp = unit.measurement.unit_temp?.toFixed(1);
				value = measUnitsConversion(temp);
				analysis = (unit.analysis.temperature_analysis_valid) ?
					unit.analysis.temperature_analysis_result : 0;
				break;
			case 3:
				value = unit.analysis.impedance_analysis_out?.toFixed(2);
				analysis = (unit.analysis.impedance_analysis_valid) ?
					unit.analysis.impedance_analysis_result : 0;
				break;
			default:
				unit.measurement.unit_voltage?.toFixed(3);
				analysis = (unit.analysis.voltage_analysis_valid) ?
					unit.analysis.voltage_analysis_result : 0;
			break;
		}
		value = (value !== null && value !== undefined && value > DATA_ERROR_VALUE.INVALID_VALUE) ? value : 0;
		let analysisColor = AnalysisResultToStateColor(analysis);

		let color = unit.unit_replaced ? tinycolor(analysisColor).darken(25).toString() : analysisColor;

		return {value, color};
	}

	function GetGraphTypeText(graphType) {
		switch(graphType) {
			case 0:
				return "Unit Voltage Trend";
			case 1:
				return "Unit Impedance Trend";
			case 2:
				return "Unit Temperature Trend";
			case 3:
				return "Unit Differential Imepdance Trend";
			default:
				return "Unit Voltage Trend";
		}
	}
	
	
	function GetGraphData(trendData, graphType) {
		// Pick the units to graph based on the selected string
		if (trendData && trendData.length > 0) {
			
			const filteredData = trendData.map((measurement) => {
				const { value, color } = GetUnitInfo(measurement, graphType);
				
				return {
					label: formatDateTime(measurement.measurement_date),
					value: value,
					barColor: color + "77",  // Make the bar color partially transparent
					borderColor: color,  // The outline should be fully opaque
				};
			});

			const graphTypeText = GetGraphTypeText(graphType);
			
			// Add measurement data to datasets
			let dataSets = []
			dataSets.push({
				type: graphStyle === GRAPH_STYLE.POINT ? 'scatter' : 'line',
				label: graphTypeText,
				backgroundColor: filteredData.map((unit) => unit.barColor),
				borderColor: filteredData.map((unit) => unit.borderColor),
				segment: {
					borderColor: (ctx) => (filteredData[ctx.p1DataIndex].barColor),
					backgroundColor: (ctx) => (filteredData[ctx.p1DataIndex].barColor),
				},
				data: filteredData.map((unit) => unit.value),
				fill: graphStyle === GRAPH_STYLE.AREA,
				order: 10,
			});
			
			// check for max/min/avg enabled and add data to datasets if enabled
			if (graphSettings.maxminEnabled || graphSettings.avgEnabled) {
				const maxavgminData = GetMaxAvgMinData(graphType);
				if (maxavgminData !== undefined && !isEmpty(maxavgminData)) {
					if (graphSettings.maxminEnabled) {
						if (maxavgminData.maxSet !== undefined) { dataSets.push(maxavgminData.maxSet); }
						if (maxavgminData.minSet !== undefined) { dataSets.push(maxavgminData.minSet); }
					}

					if (graphSettings.avgEnabled && maxavgminData.minSet !== undefined && maxavgminData.minSet != {}) {
						dataSets.push(maxavgminData.avgSet);
					}
				}
			}

			return {
				labels: filteredData.map((unit) => unit.label),
				datasets: dataSets,
			};
		}

		return null;
	}

	const handleLimitMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			limitsEnabled: !graphSettings.limitsEnabled})
		);
	};

	const handleMaxMinMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			maxminEnabled: !graphSettings.maxminEnabled})
		);
	};

	const handleAvgMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			avgEnabled: !graphSettings.avgEnabled})
		);
	};

	const handleInitialMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			initialsEnabled: !graphSettings.initialsEnabled})
		);
	};

	const handleGraphResetZoom = () => {
		chartRef.current.resetZoom();
	};

	const handleGraphStyle = (event, newStyle) => {
		if (newStyle !== null) {
			setGraphStyle(newStyle);
		}
	};

	return (
		<div
			style={{
				width: '100%',
				height: '99%',
				display: 'flex',
				flexDirection: 'column',
				position: 'relative',
			}}
		>
			<div style={{ height: '100%', width: '100%' }}>
				{ (graphData !== undefined && graphData !== null && graphData.datasets) &&
					<Chart
						ref={chartRef}
						type='line'
						options={graphOptions}
						data={graphData}
						plugins = {graphPlugins}
					/>
				}
			</div>
			<Grid container spacing={1}>
				<Grid item xs alignItems="left">
					<ToggleButtonGroup
						value={[]}
						onChange={() => {}}
						aria-label="graph markers shown"
						size='small'
						sx={{ padding: '6px' }}
					>
						<ToggleButton
							value="limitMarkers"
							selected={graphSettings.limitsEnabled}
							onClick={handleLimitMarkersToggle}
						>
							Limits
						</ToggleButton>
						<ToggleButton
							value="maxminMarkers"
							selected={graphSettings.maxminEnabled}
							onClick={handleMaxMinMarkersToggle}
						>
							Max/Min
						</ToggleButton>
						<ToggleButton
							value="avgMarkers"
							selected={graphSettings.avgEnabled}
							onClick={handleAvgMarkersToggle}
						>
							Avg
						</ToggleButton>
						{ graphType === 1 &&
							<ToggleButton
								value="initialMarkers"
								selected={graphSettings.initialsEnabled && graphType === 1}
								onClick={handleInitialMarkersToggle}
								disabled={graphType !== 1}
							>
								Initials
							</ToggleButton>
						}
					</ToggleButtonGroup>
				</Grid>
				<Grid item xs alignItems="center">
					<Button
						variant="contained"
						startIcon={<CenterFocusStrongIcon />}
						onClick={handleGraphResetZoom}
						sx={{
							marginY: 1,
							backgroundColor: 'white',
							border: '1px solid lightgray',
							color: 'black',
							":hover": {
								backgroundColor: 'lightgray',
								boxShadow: 'none',
							}

						}}
					>
						Reset View
					</Button>
				</Grid>
				<Grid item>
					<ToggleButtonGroup
						value={graphStyle}
						onChange={handleGraphStyle}
						exclusive
						aria-label="graph marker type"
						size='small'
						sx={{ padding: '6px' }}
					>
						<ToggleButton
							value={GRAPH_STYLE.AREA}
							aria-label="Area Graph Style"
						>
							Area
						</ToggleButton>
						<ToggleButton
							value={GRAPH_STYLE.LINE}
							aria-label="Line Graph Style"
						>
							Line
						</ToggleButton>
						<ToggleButton
							value={GRAPH_STYLE.POINT}
							aria-label="Point Graph Style"
						>
							Point
						</ToggleButton>
					</ToggleButtonGroup>
				</Grid>
			</Grid>
		</div>
	);
}